import React, { useContext, useEffect, useRef } from "react";
import { Spin } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthContext from "../../context/AuthContext";

// Literal path: importing the routes map here would create an import cycle (routes -> pages -> this page).
const HOME_PATH = "/";

export const SSO_ERROR_MESSAGES: Record<string, string> = {
  not_authorized: "No active account for this Microsoft user. Contact an administrator.",
  state_mismatch: "Your Microsoft sign-in expired or was interrupted. Please try again.",
  sso_failed: "Microsoft sign-in failed. Please try again.",
};

/**
 * Landing page after the server's Microsoft callback. On success the server has
 * already set the refresh cookie: AuthProvider's mount-time session recovery
 * exchanges it for an access token and PublicRoute then redirects into the app.
 * This page only reports failures and clears the query string.
 */
export const SsoCallbackPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isLoading, validateToken, reportError } = useContext(AuthContext);
  const errorCode = params.get("error");
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    if (errorCode) {
      handled.current = true;
      reportError(SSO_ERROR_MESSAGES[errorCode] ?? SSO_ERROR_MESSAGES.sso_failed);
      navigate(HOME_PATH, { replace: true });
      return;
    }
    // Session recovery finished but produced no session (cookie missing or rejected).
    if (!isLoading && !validateToken()) {
      handled.current = true;
      reportError(SSO_ERROR_MESSAGES.sso_failed);
      navigate(HOME_PATH, { replace: true });
    }
  }, [errorCode, isLoading, validateToken, reportError, navigate]);

  return (
    <div role="status" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginTop: "20%" }}>
      <Spin />
      <span>Signing you in…</span>
    </div>
  );
};
